import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Optional,
  Self,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, FormControl, NgControl} from '@angular/forms';
import {MatFormFieldControl, MatFormFieldModule} from '@angular/material/form-field';
import {Subject} from 'rxjs';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {MatDatepicker, MatDatepickerModule} from '@angular/material/datepicker';
import {MatTimepickerModule} from '@angular/material/timepicker';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import dayjs from 'dayjs/esm';
import {DAYJS_DATETIME_FORMATS} from '../../adapters/dayjs-date-formats';

type Dayjs = dayjs.Dayjs;

// NOTE: this component is built around Dayjs values (.format / .clone / .hour
// on internal state). It REQUIRES the consuming app to provide
// DayJsDateAdapter as the root DateAdapter. It does not set the adapter
// itself — shared libraries consume whichever adapter the app wires up.
@Component({
  selector: 'xui-mat-datetime',
  exportAs: 'matDatetime',
  templateUrl: './mat-datetime.component.html',
  styleUrl: './mat-datetime.component.scss',
  providers: [
    {provide: MatFormFieldControl, useExisting: MatDatetimeComponent},
    {provide: MAT_DATE_FORMATS, useValue: DAYJS_DATETIME_FORMATS}
  ],
  imports: [
    MatFormFieldModule, MatInputModule, MatDatepickerModule, MatTimepickerModule,
    MatButtonModule, FormsModule, ReactiveFormsModule
  ]
})
export class MatDatetimeComponent implements MatFormFieldControl<Dayjs | null>, ControlValueAccessor, OnDestroy, AfterViewInit {
  static nextId = 0;

  private elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-describedby') userAriaDescribedBy: string;
  @Input() minDate: Dayjs | null = null;
  @Input() maxDate: Dayjs | null = null;

  @ViewChild('picker', { static: false }) picker!: MatDatepicker<Dayjs>;

  @HostBinding() id = `app-mat-datetime-${MatDatetimeComponent.nextId++}`;

  dateControl = new FormControl<Dayjs | null>(null);
  timeValue: Dayjs | null = dayjs().hour(12).minute(0).second(0).millisecond(0);
  stateChanges = new Subject<void>();
  focused = false;
  controlType = 'app-mat-datetime';
  autofilled?: boolean;

  private _placeholder: string;
  private _required = false;
  private _disabled = false;
  private _touched = false;

  onChange = (_: any) => {};
  onTouched = () => {};

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  get empty() {
    return !this.dateControl.value;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    return this.ngControl && this.ngControl.errors !== null && this._touched;
  }

  @Input()
  get value(): Dayjs | null {
    return this.dateControl.value;
  }

  set value(date: Dayjs | null) {
    if (date && dayjs.isDayjs(date)) {
      // Sync timeValue with the date's time
      this.timeValue = date.clone();
    }
    this.dateControl.setValue(date);
    this.stateChanges.next();
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }

  set placeholder(placeholder: string) {
    this._placeholder = placeholder;
    this.stateChanges.next();
  }

  @Input()
  get required() {
    return this._required;
  }

  set required(required: boolean) {
    this._required = coerceBooleanProperty(required);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    if (this._disabled) { this.dateControl.disable(); } else { this.dateControl.enable(); }
    this.stateChanges.next();
  }

  ngAfterViewInit(): void {
    // Defer change detection to next tick to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.cdr.markForCheck(), 0);
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  togglePicker(): void {
    if (this.picker) {
      this.picker.open();
    }
  }

  setDescribedByIds(ids: string[]): void {
    const controlElement = this.elementRef.nativeElement.querySelector('input');
    if (controlElement) {
      controlElement.setAttribute('aria-describedby', ids.join(' '));
    }
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elementRef.nativeElement.querySelector('input')?.focus();
    }
  }

  onFocusIn() {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this._touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  onDateChange(date: Dayjs | Date | null) {
    if (date) {
      const coerced = dayjs.isDayjs(date) ? date : dayjs(date);
      if (!coerced.isValid()) {
        this.value = null;
      } else {
        // Preserve existing time when date changes, or use default (12:00) if
        // not set. Defensively coerce timeValue — it should always be a valid
        // Dayjs (the time input is bound one-way and we only assign coerced
        // values inside onTimeChange), but a third party could mutate it.
        const fallback = dayjs().hour(12).minute(0);
        const rawTime = this.timeValue;
        const timeCandidate = rawTime
          ? (dayjs.isDayjs(rawTime) ? rawTime : dayjs(rawTime))
          : fallback;
        const timeToUse = timeCandidate.isValid() ? timeCandidate : fallback;
        this.value = coerced.clone()
          .hour(timeToUse.hour())
          .minute(timeToUse.minute())
          .second(0)
          .millisecond(0);
      }
    } else {
      this.value = null;
    }
    this.onChange(this.value);
  }

  onTimeChange(time: Dayjs | Date | null) {
    if (!time) {
      return;
    }
    const coerced = dayjs.isDayjs(time) ? time : dayjs(time);
    if (!coerced.isValid()) {
      return;
    }

    // Always remember the chosen time, even when no date is committed yet.
    // The datepicker buffers the calendar selection until "Confirm Date"
    // (matDatepickerApply), so during a fresh booking dateControl.value is
    // still null while the popover is open. onDateChange reads timeValue when
    // the date is finally applied — if we skipped this assignment, the picked
    // time would be lost and the field would snap back to the 12:00 default.
    this.timeValue = coerced;

    if (this.dateControl.value) {
      const updatedDate = this.dateControl.value.clone()
        .hour(coerced.hour())
        .minute(coerced.minute())
        .second(0)
        .millisecond(0);
      this.value = updatedDate;
      this.onChange(updatedDate);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  writeValue(date: Dayjs | Date | string | null): void {
    if (date) {
      // Convert to Dayjs if needed
      this.value = dayjs.isDayjs(date) ? date : dayjs(date);
    } else {
      this.value = null;
    }
  }
}
